import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUploadFiles } from "api/image.api";
import {
  createUppdragFiles,
  deleteUppdragFile,
  patchUppdrag,
  sendUppdragAttachments,
  UppdragQueryKey,
} from "api/uppdrag.api";
import { FileUploadButton } from "components/buttons/FileUploadButton";
import { useEffect, useState } from "react";
import { InspectionType } from "types/enums";
import {
  shouldShowEmailToCustomerPing,
  Uppdrag,
  UppdragStatus,
} from "types/uppdrag.type";
import Alert from "components/Alert";
import {
  getDocumentationEmail,
  hasValidDocumentationEmail,
} from "types/facility.type";
import { useCustomerDocumentationEmail } from "components/inspection/attachments/useCustomerDocumentationEmail.logic";
import Modal from "components/Modal";
import { DocumentationEmailForm } from "components/DocumentationEmailForm";
import { usePreviewPdf } from "hooks/usePreviewPdf";
import PrimaryButton from "components/buttons/PrimaryButton";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
  ContentContainer,
  HeaderContainer,
  ShadowCard,
} from "components/ShadowCard";
import { Headline4 } from "components/typography/Headline4";
import {
  FilePreviewSmall,
  InspectionPDFPreviewSmall,
} from "components/FilePreviewSmall";
import { getFileNameNew, splitFileName } from "helpers/file";
import { HasSentStatus } from "types/transportDocument.type";
import { SentToCustomerStatusTag } from "components/SentToCustomerStatusTag";
import SecondaryButton from "components/buttons/SecondaryButton";
import { InspectionItem } from "./InspectionItem";
import { WaterMeasurementProtocolItem } from "./WaterMeasurementProtocolItem";
import { DotMenu } from "components/DotMenu";
import { Ping } from "components/Ping";
import { Info } from "components/Info";
import { useNotification } from "hooks/useNotification";
import { Headline6 } from "components/typography/Headline6";
import { useIsAdmin } from "store/user.store";
import { RapporteringsTyp } from "api/transportDocument.api";

type Props = {
  uppdrag: Uppdrag;
};
export const Customer = ({ uppdrag }: Props) => {
  const isAdmin = useIsAdmin();

  const [latestFileName, setLatestFileName] = useState<string | undefined>();
  const qc = useQueryClient();
  const createUppdragFilesMutation = useMutation({
    ...createUppdragFiles(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });
  const uploadFilesMutation = useUploadFiles();

  useEffect(() => {
    if (uploadFilesMutation.data) {
      const [nameWithoutExtension, extension] = splitFileName(latestFileName);
      createUppdragFilesMutation.mutate({
        uppdragId: uppdrag.id,
        payload: {
          files: uploadFilesMutation.data.map((x) => ({
            id: undefined,
            name: x,
            url: x,
            nameWithoutExtension,
            extension,
          })),
        },
      });
      uploadFilesMutation.reset();
    }
  }, [
    createUppdragFilesMutation,
    latestFileName,
    uploadFilesMutation,
    uppdrag.id,
  ]);

  const onFileAdd = async (blob: Blob | null, filename: string) => {
    setLatestFileName(filename);

    if (!blob) return;
    const file = new File([blob], filename);
    if (!file) {
      return;
    }

    uploadFilesMutation.mutate([file]);
  };

  const deleteMutation = useMutation({
    ...deleteUppdragFile(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });

  /// DocumentationEmail
  const {
    isDocumentationModalOpen,
    setIsDocumentationModalOpen,
    closeModal,
    onSubmit,
    register,
    errors,
    isPatchingCustomer,
  } = useCustomerDocumentationEmail(uppdrag.facility.customer);

  const { previewPdf, isLoading, currentUrl } = usePreviewPdf();

  const isReadyToSendForFirstTime =
    (uppdrag.emailToCustomerStatus === HasSentStatus.NotSent ||
      uppdrag.emailToCustomerStatus === HasSentStatus.Ignored) &&
    uppdrag.status === UppdragStatus.Completed &&
    hasValidDocumentationEmail(uppdrag.facility);

  const isReadyToSendForSecondTime =
    uppdrag.emailToCustomerStatus === HasSentStatus.Sent &&
    uppdrag.status === UppdragStatus.Completed &&
    hasValidDocumentationEmail(uppdrag.facility);

  const notification = useNotification();
  const sendMutation = useMutation({
    ...sendUppdragAttachments(),
    onSuccess: () => {
      notification.info("Bilagor skickade");
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
    },
  });

  const patchUppdragMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
    },
  });

  const hasDocumentationEmailButNotReady =
    hasValidDocumentationEmail(uppdrag.facility) &&
    uppdrag.status !== UppdragStatus.Completed;

  return (
    <ShadowCard>
      <HeaderContainer>
        <Headline4 className="flex items-center">
          Till kund
          {shouldShowEmailToCustomerPing(uppdrag) && <Ping />}
          {uppdrag.emailToCustomerStatus === HasSentStatus.Sent && (
            <SentToCustomerStatusTag
              className="ml-3"
              status={HasSentStatus.Sent}
            />
          )}
        </Headline4>
        {shouldShowEmailToCustomerPing(uppdrag) && (
          <DotMenu
            items={[
              {
                label: "Ta bort påminnelse",
                onClick: () =>
                  patchUppdragMutation.mutate({
                    id: uppdrag.id,
                    payload: {
                      emailToCustomerStatus: HasSentStatus.Ignored,
                    },
                  }),
              },
            ]}
          />
        )}
      </HeaderContainer>
      <ContentContainer className="space-y-8">
        {uppdrag.fiveYearsInspectionList.map((x) => (
          <InspectionItem
            key={x.id}
            inspection={x}
            type={InspectionType.FiveYears}
            uppdrag={uppdrag}
          />
        ))}
        {uppdrag.sixMonthsInspectionList.map((x) => (
          <InspectionItem
            key={x.id}
            inspection={x}
            type={InspectionType.SixMonths}
            uppdrag={uppdrag}
          />
        ))}
        {uppdrag.waterMeasurementProtocolList.map((x) => (
          <WaterMeasurementProtocolItem key={`wmp-${x.id}`} protocol={x} />
        ))}
        {uppdrag.transportSedelList.length > 0 && (
          <div className="flex flex-col">
            <Headline6 className="mb-2">Transportsedel</Headline6>
            {uppdrag.transportSedelList.map((x) => (
              <InspectionPDFPreviewSmall
                key={x.id}
                inspectionName="transportsedel.pdf"
                isLoading={
                  isLoading &&
                  currentUrl === `/transport-sedel/${x.id}/preview-pdf`
                }
                onClick={() =>
                  previewPdf(`/transport-sedel/${x.id}/preview-pdf`)
                }
              />
            ))}
          </div>
        )}

        {(uppdrag.ombudsRapporteringList.length > 0 ||
          uppdrag.transportRapporteringList.length > 0) && (
          <div className="flex flex-col space-y-2">
            <Headline6>
              {uppdrag.ombudsRapporteringList.length > 1
                ? "Rapporteringar till Naturvårdsverket"
                : "Rapportering till Naturvårdsverket"}
            </Headline6>
            {uppdrag.transportRapporteringList.map((x) => (
              <InspectionPDFPreviewSmall
                key={x.id}
                inspectionName={`transport_${x.nvAvfallId}.pdf`}
                isLoading={
                  isLoading &&
                  currentUrl ===
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.Transport}`
                }
                onClick={() =>
                  previewPdf(
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.Transport}`,
                  )
                }
              />
            ))}
            {uppdrag.ombudsRapporteringList.map((x) => (
              <InspectionPDFPreviewSmall
                key={x.id}
                inspectionName={`avfallsproducent_${x.nvAvfallId}.pdf`}
                isLoading={
                  isLoading &&
                  currentUrl ===
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.OmbudsRapportering}`
                }
                onClick={() =>
                  previewPdf(
                    `/waste/preview-nv-receipt/${x.nvAvfallId}?type=${RapporteringsTyp.OmbudsRapportering}`,
                  )
                }
              />
            ))}
          </div>
        )}
        <div className="space-y-2">
          <Headline6>Övriga filer</Headline6>
          {uppdrag.fileList.map((x) => (
            <FilePreviewSmall
              key={x.id}
              url={x.url}
              onDelete={() => {
                deleteMutation.mutate({ uppdragId: uppdrag.id, fileId: x.id });
              }}
              name={getFileNameNew(x)}
            />
          ))}
          <FileUploadButton
            onFileAdd={onFileAdd}
            isLoading={uploadFilesMutation.isPending}
            label={
              uppdrag.fileList.length > 0
                ? "Ladda upp en till"
                : "Ladda upp fil"
            }
          />
        </div>
        {!hasValidDocumentationEmail(uppdrag.facility) && isAdmin && (
          <div className="mt-6">
            <Alert
              text="Kunden saknar Epostadress för rapporter."
              buttonText="Lägg till"
              onButtonClick={() => setIsDocumentationModalOpen(true)}
            />
            {/* <div className="flex justify-center items-center">
                      <button
                        onClick={() => setIsDocumentationModalOpen(true)}
                        className="text-sm text-cyan-700 font-semibold underline"
                      >
                        Lägg till
                      </button>
                    </div> */}
          </div>
        )}
        {hasDocumentationEmailButNotReady && isAdmin && (
          <Info
            text={"Uppdraget behöver slutföras innan bilagorna kan skickas"}
          />
        )}
        {isReadyToSendForFirstTime && isAdmin && (
          <div className="flex flex-col items-center space-y-2">
            <PrimaryButton
              onClick={() =>
                sendMutation.mutate({
                  uppdragId: uppdrag.id,
                  payload: {
                    toCustomer: true,
                  },
                })
              }
              isLoading={sendMutation.isPending}
              loadingText="Skickar..."
              className="w-full"
            >
              <EnvelopeIcon className="mr-2 h-5 w-5" />
              {`Skicka till ${uppdrag.facility.customer.name}`}
              {/* {getSendButtonText(existingFiles, includeTransportDocument)} */}
            </PrimaryButton>
            <span className="text-xs text-gray-500">
              Skickas till
              <span className="ml-1 font-medium text-gray-900">
                {getDocumentationEmail(uppdrag.facility)}
              </span>
            </span>
          </div>
        )}

        {isReadyToSendForSecondTime && isAdmin && (
          <div className="flex flex-col items-center space-y-2">
            <SecondaryButton
              onClick={() =>
                sendMutation.mutate({
                  uppdragId: uppdrag.id,
                  payload: {
                    toCustomer: true,
                  },
                })
              }
              isLoading={sendMutation.isPending}
              loadingText="Skickar..."
              className="w-full"
            >
              <EnvelopeIcon className="mr-2 h-5 w-5" />
              Skicka igen
              {/* {getSendButtonText(existingFiles, includeTransportDocument)} */}
            </SecondaryButton>
            <span className="text-xs text-gray-500">
              Skickas till
              <span className="ml-1 font-medium text-gray-900">
                {getDocumentationEmail(uppdrag.facility)}
              </span>
            </span>
          </div>
        )}

        {/* {uppdrag && (
               <div className="flex flex-col items-center space-y-2">
                 <PrimaryButton
                   onClick={() =>
                     sendMutation.mutate({
                       uppdragId: uppdrag.id,
                       payload: {
                         toCustomer: true,
                       },
                     })
                   }
                   isLoading={sendMutation.isPending}
                   loadingText="Skickar..."
                   className="w-full"
                 >
                   <EnvelopeIcon className="mr-2 h-5 w-5" />
                   Skicka till kund
                 </PrimaryButton>
                 <span className="text-sm text-gray-500">
                   Skickas till
                   <span className="ml-1 font-medium text-gray-900">
                     {getDocumentationEmail(uppdrag.facility)}
                   </span>
                 </span>
               </div>
             )} */}

        <Modal
          isOpen={isDocumentationModalOpen}
          onClose={closeModal}
          title="Ändra kund"
          actionText="Spara"
          actionHandler={onSubmit}
          isLoading={isPatchingCustomer}
          loadingText="Sparar..."
        >
          <DocumentationEmailForm
            register={register}
            errors={errors}
            customerName={uppdrag.facility.customer.name}
          />
        </Modal>
      </ContentContainer>
    </ShadowCard>
  );
};
